import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { Trash2Icon, UploadIcon } from 'lucide-react'

import { ExtractedStorageResource } from '@campai/lib-common/entities'
import { formatBytes } from '@campai/lib-common/i18n'

import { selectFile } from '@/utils/storage'
import { Button, FieldLabel } from '@/components/new/ui'
import { UploadHandler } from '@/components/useUpload'

import { Image } from './Image'
import { ImageAspectRatio, ImageScaleMode, ImageShape } from './types'
import { ImageEditorDialog } from './ImageEditor'
import { EditConfig } from './ImageEditor/types'

const imageMimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

type ImageStorageResourceSelectSizeProps =
    | {
          imageWidthClassName?: never
          imageHeightClassName: string
      }
    | {
          imageWidthClassName: string
          imageHeightClassName?: never
      }

type ImageStorageResourceSelectProps = ImageStorageResourceSelectSizeProps & {
    error?: string
    label?: string
    info?: string
    hintPosition?: 'bottom' | 'right' | 'info'
    iconButtons?: boolean
    required?: boolean
    small?: boolean
    maxSize: number
    aspectRatio: ImageAspectRatio
    /**
     * Override crop ratio; set to null to allow freeform.
     * Defaults to aspectRatio when undefined.
     */
    cropAspectRatio?: ImageAspectRatio | null
    scaleMode?: ImageScaleMode
    shape?: ImageShape
    allowClear?: boolean
    disabled?: boolean
    uploadButtonLabel?: string
    value: ExtractedStorageResource | null
    onChange: (value: ExtractedStorageResource | null) => void
    upload: UploadHandler
}

export const ImageStorageResourceSelect = ({
    imageWidthClassName,
    imageHeightClassName,
    maxSize,
    error,
    label,
    info,
    hintPosition = 'bottom',
    iconButtons,
    small,
    required,
    aspectRatio,
    cropAspectRatio,
    scaleMode = 'cover',
    shape = 'rounded',
    allowClear = true,
    disabled,
    uploadButtonLabel,
    value,
    onChange,
    upload,
}: ImageStorageResourceSelectProps) => {
    const { t, i18n } = useTranslation()

    const i18nKey = 'components.imageStorageResourceSelect'

    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [editingFile, setEditingFile] = useState<File | null>(null)
    const [isUploading, setUploading] = useState(false)

    const imageEditConfig = useMemo<EditConfig>(() => {
        const resolvedCropAspectRatio = cropAspectRatio === null ? undefined : (cropAspectRatio ?? aspectRatio)

        return {
            image: { shape, compress: true, maxSize, format: 'webp' },
            crop: resolvedCropAspectRatio ? { aspectRatio: resolvedCropAspectRatio } : {},
        }
    }, [aspectRatio, cropAspectRatio, maxSize, shape])

    const handleSelectFile = useCallback(() => {
        const doSelect = async () => {
            const file = await selectFile(imageMimeTypes)

            if (file) {
                setEditingFile(file)
            }
        }

        doSelect()
    }, [])

    const handleEditorOpenChange = useCallback((open: boolean) => {
        if (!open) {
            setEditingFile(null)
        }
    }, [])

    const handleEditorSubmit = useCallback(
        async (image: Blob) => {
            setEditingFile(null)

            try {
                setUploading(true)

                const uploadResult = await upload(image)

                if (uploadResult) {
                    setSelectedFile(uploadResult.file)

                    onChange({
                        url: uploadResult.url,
                        resource: uploadResult.fileId,
                        fileName: uploadResult.fileName,
                    })
                }
            } finally {
                setUploading(false)
            }
        },
        [onChange, upload]
    )

    const handleClear = useCallback(() => {
        setSelectedFile(null)
        onChange(null)
    }, [onChange])

    const handleView = useCallback(() => {
        if (value) {
            window.open(value.url, '_blank')
        }
    }, [value])

    let hint: ReactNode = ''

    if (error) {
        hint = <span className="text-error">{error}</span>
    } else if (selectedFile) {
        hint = (
            <>
                {selectedFile.name && (
                    <>
                        <span>{selectedFile.name}</span>
                        <span>&nbsp;&ndash;&nbsp;</span>
                    </>
                )}
                <span>{formatBytes(selectedFile.size, { i18n })}</span>
            </>
        )
    } else if (value && value.fileName) {
        hint = (
            <>
                <span>{value.fileName}</span>
                {value.fileSizeBytes && (
                    <>
                        <span>&nbsp;&ndash;&nbsp;</span>
                        <span>{formatBytes(value.fileSizeBytes, { i18n })}</span>
                    </>
                )}
            </>
        )
    }

    const selectCaption = uploadButtonLabel || (value ? t(`${i18nKey}.change`) : t(`${i18nKey}.select`))
    const buttonSize = small ? 'sm' : 'default'
    const iconButtonSize = small ? 'icon-sm' : 'icon'

    return (
        <div>
            {editingFile && (
                <ImageEditorDialog
                    open={!!editingFile}
                    image={editingFile}
                    config={imageEditConfig}
                    onOpenChange={handleEditorOpenChange}
                    onSubmit={handleEditorSubmit}
                />
            )}
            {label && (
                <FieldLabel
                    className={classNames('mb-1.5', {
                        'text-destructive': hintPosition === 'info' && !!error,
                    })}
                    hint={hintPosition === 'info' ? hint : info}
                    required={required}
                >
                    {label}
                </FieldLabel>
            )}
            <div
                className={classNames('flex gap-x-4', {
                    'items-center': hintPosition === 'info',
                    'items-start': hintPosition !== 'info',
                })}
            >
                <div className="shrink-0 cursor-pointer" onClick={value?.url ? handleView : undefined}>
                    {/** @ts-expect-error its fine its only TS issues */}
                    <Image
                        imageWidthClassName={imageWidthClassName}
                        imageHeightClassName={imageHeightClassName}
                        aspectRatio={aspectRatio}
                        scaleMode={scaleMode || 'cover'}
                        shape={shape}
                        url={value?.url}
                    />
                </div>
                <div
                    className={classNames('flex gap-2 *:shrink-0', {
                        'flex-col': hintPosition === 'bottom',
                        'items-center': hintPosition === 'right',
                    })}
                >
                    <div className="flex shrink-0 items-center gap-x-2">
                        <Button
                            variant="outline"
                            size={iconButtons ? iconButtonSize : buttonSize}
                            title={iconButtons ? selectCaption : undefined}
                            onClick={handleSelectFile}
                            disabled={disabled}
                            loading={isUploading}
                        >
                            {iconButtons ? <UploadIcon /> : selectCaption}
                        </Button>
                        {allowClear && value && (
                            <Button
                                variant="outline"
                                size={iconButtons ? iconButtonSize : buttonSize}
                                title={iconButtons ? t(`${i18nKey}.clear`) : undefined}
                                onClick={handleClear}
                                disabled={disabled || isUploading}
                            >
                                {iconButtons ? <Trash2Icon /> : t(`${i18nKey}.clear`)}
                            </Button>
                        )}
                    </div>
                    {hint && hintPosition !== 'info' && (
                        <div className="text-muted-foreground moverflow-hidden text-ellipsis whitespace-nowrap text-xs leading-5">
                            {hint}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
